<template>
  <header
    :aria-label="t('readerTitleBar.componentTitle')"
    class="topbar flex justify-between items-center animate noselect"
    :class="[
      !visibilityStore.showUI ? 'hidden-bar' : null,
      wpsClient.interactive && !visibilityStore.showUI
        ? 'topbar--interactive-hidden'
        : null,
    ]"
    @mouseover="
      attributesStore.screenHasHover &&
      !wpsClient.interactive &&
      !textSelected()
        ? visibilityStore.setShowUI(true)
        : null
    "
    @click="
      attributesStore.screenHasHover || wpsClient.interactive
        ? null
        : visibilityStore.setShowUI(true)
    "
  >
    <div class="topbar__arrow-back">
      <a
        v-if="urlGoBack"
        :href="urlGoBack"
        :disabled="!visibilityStore.showUI"
        class="square-icon-button items-center"
        :title="t('readerTitleBar.tooltipGoBack')"
        :aria-label="t('readerTitleBar.arrowBack')"
      >
        <span class="material-icons" aria-hidden="true">arrow_back</span>
      </a>
    </div>
    <div class="topbar__title truncate-container">
      <span
        v-if="offlineStore.offlineLoading"
        class="spinner-border spinner-border--text animate-spin inline-block w-4 h-4 rounded-full"
        role="status"
        :aria-label="t('readerTitleBar.offlineLoading')"
        tabindex="0"
      >
      </span>
      <span
        v-else-if="offlineStore.offlineActive"
        class="topbar__title-icon material-icons"
        role="status"
        :aria-label="t('readerTitleBar.offline')"
        tabindex="0"
      >
        cloud_off
      </span>
      <h1
        :aria-label="t('readerTitleBar.title') + title"
        class="truncate text-center"
        :class="
          offlineStore.offlineLoading || offlineStore.offlineActive
            ? 'topbar__title--offline'
            : null
        "
        tabindex="0"
      >
        <span class="topbar__title-text">{{ title }} </span>
      </h1>
      <span v-if="offlineStore.offlineLoading">
        {{ `${offlineStore.offlineProgress}%` }}
      </span>
    </div>
    <nav
      class="flex flex-1 justify-end"
      :aria-label="t('readerTitleBar.navigationBar')"
    >
      <ShowSettingsCommand v-slot="{ disabled, icon, label, toolTip }">
        <AccessibleButton
          class="square-icon-button justify-center items-center"
          :disabled="!visibilityStore.showUI || disabled"
          :title="toolTip"
          :aria-label="label"
          show-on-hover
        >
          <template #icon>
            <span class="material-icons" aria-hidden="true">{{ icon }}</span>
          </template>
        </AccessibleButton>
      </ShowSettingsCommand>

      <template v-if="wpsClient.token.uiEnabled">
        <ReadAloudCommand
          v-if="wpsClient.token.showcaseTTS"
          v-slot="{ disabled, icon, label, toolTip }"
        >
          <AccessibleButton
            class="square-icon-button justify-center items-center"
            :disabled="disabled || !visibilityStore.showUI"
            :title="toolTip"
            :aria-label="
              toolTip ||
              label /* toolTip is the most descriptive, label will be used as fallback */
            "
            show-on-hover
          >
            <template #icon>
              <span class="material-symbols-outlined" aria-hidden="true">
                {{ icon }}
              </span>
            </template>
          </AccessibleButton>
        </ReadAloudCommand>
        <FullscreenCommand
          v-if="!attributesStore.isMobile"
          v-slot="{ disabled, icon, label, toolTip }"
          ><AccessibleButton
            class="square-icon-button justify-center items-center"
            :disabled="disabled || !visibilityStore.showUI"
            :title="toolTip"
            :aria-label="label"
            show-on-hover
          >
            <template #icon>
              <span class="material-icons" aria-hidden="true">
                {{ icon }}
              </span>
            </template>
          </AccessibleButton>
        </FullscreenCommand>
        <TableOfContentCommand v-slot="{ disabled, icon, label, toolTip }">
          <AccessibleButton
            class="square-icon-button justify-center items-center"
            :disabled="disabled || !visibilityStore.showUI"
            :title="toolTip"
            :aria-label="label"
            show-on-hover
          >
            <template #icon>
              <span class="material-icons" aria-hidden="true">
                {{ icon }}
              </span>
            </template>
          </AccessibleButton>
        </TableOfContentCommand>
        <ShowMoreCommand v-slot="{ disabled, icon, label, toolTip }">
          <AccessibleButton
            class="square-icon-button justify-center items-center"
            :disabled="disabled || !visibilityStore.showUI"
            :title="toolTip"
            :aria-label="label"
            show-on-hover
          >
            <template #icon>
              <span class="material-icons" aria-hidden="true">
                {{ icon }}
              </span>
            </template>
          </AccessibleButton>
        </ShowMoreCommand>
      </template>
    </nav>
  </header>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n";

import { useAttributesStore } from "@/stores/attributes";
import { useUiVisibilityStore } from "@/stores/uiVisibility";
import { useOfflineStore } from "@/stores/offlineState";

import { useWpsClient } from "@/wps/client";

import AccessibleButton from "@/components/shared/AccessibleButton.vue";
import FullscreenCommand from "@/commands/ui/FullscreenCommand.vue";
import ShowMoreCommand from "@/commands/ui/ShowMoreCommand.vue";
import TableOfContentCommand from "@/commands/ui/TableOfContentCommand.vue";
import ReadAloudCommand from "@/commands/tts/ReadAloudCommand.vue";
import ShowSettingsCommand from "@/commands/ui/ShowSettingsCommand.vue";

const { t } = useI18n();

const attributesStore = useAttributesStore();
const visibilityStore = useUiVisibilityStore();
const offlineStore = useOfflineStore();

const wpsClient = useWpsClient();

interface Props {
  urlGoBack?: string | null;
  title?: string;
}

withDefaults(defineProps<Props>(), {
  urlGoBack: null,
  title: "",
});

function textSelected(): boolean {
  return window.getSelection()?.toString()?.length > 0;
}
</script>

<style lang="scss" scoped>
@use "@/assets/styles/variables.scss" as *;

.topbar {
  top: var(--safe-area-t);
  z-index: 2;
  &:hover {
    z-index: 3;
  }
  &--interactive-hidden {
    display: none;
  }
  &__arrow-back {
    @media (min-width: $screenSM) {
      flex: 1;
    }
  }
  &__title {
    padding: 0 $spacingM;
    display: flex;
    align-items: center;
    justify-content: center;
    @media (min-width: $screenSM) {
      flex: 1;
      justify-content: center;
    }
    &-icon {
      font-size: 1rem;
      margin-right: $spacingS;
      line-height: 1;
      margin-top: -3px;
    }
    &--offline {
      // Add a padding right equal to the width of the offline icon so the title will match the shadow title
      padding-right: calc(1rem + $spacingS);
    }
  }
}
</style>
